/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import React, { type ReactElement, useState, useEffect, useMemo, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Link } from '@/components/link';
import { DynamicImage } from '@/components/dynamic-image';
import { useTranslation } from 'react-i18next';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    type CarouselApi,
} from '@salesforce/storefront-ui/components/ui/carousel';
import { Button } from '@salesforce/storefront-ui/components/ui/button';
import { ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import withSuspense from '@/components/with-suspense';
import HeroCarouselSkeleton from './skeleton';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { usePageDesignerMode } from '@salesforce/storefront-next-runtime/design/react/core';
import { typographyVariants } from '@/components/typography';
import { normalizeOverlayPosition, normalizeOverlayAlignment, overlayPositionLayout } from '@/components/hero/utils';
import type { ComponentType } from '@/components/region';
import { Component as RegionComponent } from '@/components/region/component';

/** Each slide is edge-to-edge at every breakpoint, so the image always requests a viewport-width variant from DIS. */
const HERO_IMAGE_WIDTHS = ['100vw'];

const heroCarouselDefaults = {
    autoPlay: true,
    autoPlayInterval: 5000,
    showDots: true,
    showNavigation: true,
    /** Default gradient scrim applied to every slide; each Hero slide can override its own `overlay`. */
    overlay: 'Dark',
} as const;

@Component('heroCarousel', {
    name: 'Hero Carousel',
    description:
        'Interactive carousel component with multiple hero slides, autoplay, navigation controls, and dot indicators',
    group: 'Layout',
})
@RegionDefinition([
    {
        id: 'slides',
        name: 'Carousel Slides',
        description:
            'Add hero components to display as carousel slides. Each hero will be shown as a full-width slide.',
        maxComponents: 10,
        componentTypeInclusions: ['Content.hero'],
    },
])
// oxlint-disable-next-line react/only-export-components -- oxlint flags the co-exported Page Designer metadata class; eslint-plugin-react-refresh does not
export class HeroCarouselMetadata {
    @AttributeDefinition({ defaultValue: heroCarouselDefaults.autoPlay })
    autoPlay?: boolean;

    @AttributeDefinition({ defaultValue: heroCarouselDefaults.autoPlayInterval })
    autoPlayInterval?: number;

    @AttributeDefinition({ defaultValue: heroCarouselDefaults.showDots })
    showDots?: boolean;

    @AttributeDefinition({ defaultValue: heroCarouselDefaults.showNavigation })
    showNavigation?: boolean;

    @AttributeDefinition({
        id: 'overlay',
        name: 'Slide Overlay',
        description:
            'Default gradient scrim applied to every slide to keep text legible. Individual Hero slides can override this with their own Overlay setting.',
        type: 'enum',
        values: ['None', 'Light', 'Dark'],
        defaultValue: heroCarouselDefaults.overlay,
    })
    overlay?: string;
}

type Image = {
    url: string;
    metaData?: {
        height?: number | string;
        width?: number | string;
    };
    focalPoint?: {
        x?: number | string;
        y?: number | string;
    };
};

/** Stable empty-array reference so the `slides` default never changes identity between renders. */
const NO_SLIDES: HeroSlide[] = [];

/** `Heading 2` preset (shared Typography scale) for the empty-state "No banners yet" cue. */
const EMPTY_LABEL_CLASS = typographyVariants({ variant: 'h2', align: 'center' });

export interface HeroSlide {
    id: string;
    title: string;
    subtitle?: string;
    imageUrl: string;
    imageAlt?: string;
    ctaText?: string;
    ctaLink?: string;
    /**
     * Accessible name for the CTA link, overriding the visible ctaText. Use when ctaText is
     * generic (e.g. "Shop Now") and does not by itself describe the link's destination. WCAG 2.4.4.
     */
    ctaAriaLabel?: string;
    overlayPosition?: string;
    overlayAlignment?: string;
}

interface HeroCarouselProps {
    slides?: HeroSlide[];
    image?: Image;
    autoPlay?: boolean;
    autoPlayInterval?: number;
    showDots?: boolean;
    showNavigation?: boolean;
    /** Default gradient scrim applied to every slide; a per-slide Hero `overlay` overrides it. */
    overlay?: string;
    /** Component data containing regions from Page Designer */
    component?: ComponentType;
}

export function HeroCarouselPlain({
    slides: propSlides = NO_SLIDES,
    autoPlay = heroCarouselDefaults.autoPlay,
    image,
    autoPlayInterval = heroCarouselDefaults.autoPlayInterval,
    showDots = heroCarouselDefaults.showDots,
    showNavigation = heroCarouselDefaults.showNavigation,
    overlay = heroCarouselDefaults.overlay,
    component,
}: HeroCarouselProps): ReactElement | null {
    // Production (Page Designer) path: render each slide by delegating to the real Hero
    // component through the <Component> registry, so every authored Hero attribute (typography,
    // colors, button style, focal point, styleOverride, …) is honored — instead of flattening a
    // handful of fields into a bespoke slide renderer. Mirrors product-carousel's region path.
    const regionComponents = useMemo(() => {
        if (!Array.isArray(component?.regions)) return [];
        const slidesRegion = component.regions.find((r) => r.id === 'slides');
        if (!Array.isArray(slidesRegion?.components)) return [];
        return slidesRegion.components.filter((comp) => comp.id && comp.typeId) as ComponentType[];
    }, [component]);

    // When the region has authored heroes we delegate to <Component>; otherwise we fall back to
    // the `slides` prop (storybook/test path) rendered by the local HeroSlideContent.
    const usingRegion = regionComponents.length > 0;
    const slides = propSlides;
    const { t } = useTranslation('common');
    const { isDesignMode } = usePageDesignerMode();

    // Unified per-slide metadata (id + title) for dot indicators and the aria-live announcement,
    // independent of which render path is active.
    const slideMeta = useMemo(
        () =>
            usingRegion
                ? regionComponents.map((comp) => ({
                      id: comp.id,
                      title: ((comp.data as Record<string, unknown> | undefined)?.title as string) || '',
                  }))
                : slides.map((slide) => ({ id: slide.id, title: slide.title })),
        [usingRegion, regionComponents, slides]
    );
    const slideCount = slideMeta.length;
    const [currentSlide, setCurrentSlide] = useState(0);
    const [api, setApi] = useState<CarouselApi | null>(null);
    const [isPaused, setIsPaused] = useState(false);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [isManuallyPaused, setIsManuallyPaused] = useState(false);

    // Check for prefers-reduced-motion
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        if (mediaQuery.matches && autoPlay) {
            setIsManuallyPaused(true);
        }
    }, [autoPlay]);

    useEffect(() => {
        if (!autoPlay || !api || isPaused || isManuallyPaused) return;

        const interval = setInterval(() => {
            api.scrollNext();
        }, autoPlayInterval);

        return () => clearInterval(interval);
    }, [api, autoPlay, autoPlayInterval, isPaused, isManuallyPaused]);

    const togglePlayPause = useCallback(() => {
        setIsManuallyPaused((prev) => !prev);
    }, []);

    const onSelect = useCallback(() => {
        if (!api) return;

        const currentIndex = api.selectedScrollSnap();
        const canPrev = api.canScrollPrev();
        const canNext = api.canScrollNext();

        setCurrentSlide(currentIndex);
        setCanScrollPrev(canPrev);
        setCanScrollNext(canNext);
    }, [api]);

    useEffect(() => {
        if (!api) return;
        onSelect();
        api.on('select', onSelect);
        api.on('reInit', onSelect);

        return () => {
            api.off('select', onSelect);
            api.off('reInit', onSelect);
        };
    }, [api, onSelect]);

    const goToSlide = useCallback(
        (index: number) => {
            if (!api || index < 0 || index >= slideCount) return;

            api.scrollTo(index);
        },
        [api, slideCount]
    );

    const handleFocus = useCallback(() => setIsPaused(true), []);
    const handleBlur = useCallback(() => setIsPaused(false), []);
    const handleMouseEnter = useCallback(() => setIsPaused(true), []);
    const handleMouseLeave = useCallback(() => setIsPaused(false), []);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (!api) return;

            switch (event.key) {
                case 'ArrowLeft':
                    event.preventDefault();
                    api.scrollPrev();
                    break;
                case 'ArrowRight':
                    event.preventDefault();
                    api.scrollNext();
                    break;
                case 'Home':
                    event.preventDefault();
                    api.scrollTo(0);
                    break;
                case 'End':
                    event.preventDefault();
                    api.scrollTo(slideCount - 1);
                    break;
            }
        },
        [api, slideCount]
    );

    // Empty state (W-23729831): a freshly-dropped, unconfigured Hero Carousel with no authored
    // slides yet. Renders the carousel's own design-mode placeholder — a muted surface reading
    // "No banners yet", flanked by decorative prev/next arrows and dot indicators — so the merchant
    // sees the carousel's shape before adding Hero slides. The carousel deliberately renders NONE of
    // the registered `Hero` component here: importing `Hero` (a Page Designer component) dragged its
    // registration side effects into the carousel's evaluation and could hang the whole PD region on
    // MRT when an unconfigured carousel was dropped in. Page-Designer *authoring* affordance only: on
    // the live storefront an unconfigured carousel renders nothing, so shoppers never see a
    // placeholder. Mirrors the Product/Categories carousel gates.
    if (slideCount === 0) {
        if (!isDesignMode) {
            return null;
        }
        return <HeroCarouselEmptyState label={t('heroCarousel.emptyLabel')} />;
    }

    return (
        // oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- labelled carousel region: keydown/hover handlers pause autoplay and drive arrow-key slide nav, not the primary control
        <div
            data-slot="hero-carousel"
            className="relative w-full overflow-hidden h-[400px] md:h-[500px] lg:h-[600px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
            role="region"
            aria-label={`Hero carousel with ${slideCount} slides`}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onKeyDown={handleKeyDown}
            // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- intentional tab stop so keyboard users can reach the carousel's arrow-key slide navigation
            tabIndex={0}>
            <Carousel
                setApi={setApi}
                opts={{
                    align: 'center',
                    loop: true,
                    containScroll: 'trimSnaps',
                }}
                className="w-full h-full [&_[data-slot=carousel-content]]:h-full [&_[data-slot=carousel-item]]:h-full">
                {/* Passing -ml-4 to the CarouselContent to prevent CLS issues during hydration */}
                <CarouselContent className="h-full">
                    {usingRegion
                        ? regionComponents.map((comp, index) => (
                              <CarouselItem
                                  key={comp.contentLinkUuid ?? comp.id}
                                  className="h-full"
                                  aria-hidden={index !== currentSlide}
                                  inert={index !== currentSlide ? true : undefined}>
                                  <RegionComponent
                                      component={withSlideProps(comp, {
                                          overlay,
                                          priority: index === 0 ? 'high' : 'auto',
                                          loading: index === 0 ? 'eager' : 'lazy',
                                          fillHeight: true,
                                      })}
                                      regionId="slides"
                                      className="h-full w-full"
                                  />
                              </CarouselItem>
                          ))
                        : slides.map((slide, index) => (
                              <CarouselItem
                                  key={slide.id}
                                  className="h-full"
                                  aria-hidden={index !== currentSlide}
                                  inert={index !== currentSlide ? true : undefined}>
                                  <HeroSlideContent
                                      slide={image ? { ...slide, imageUrl: image.url } : slide}
                                      priority={index === 0}
                                  />
                              </CarouselItem>
                          ))}
                </CarouselContent>
            </Carousel>

            {slideCount > 1 && (
                <div className="absolute bottom-6 inset-x-0 z-30 section-container">
                    <div className="relative flex items-center justify-center">
                        {showDots && (
                            <div className="flex gap-2" role="tablist" aria-label="Slide navigation">
                                {slideMeta.map((slide, index) => (
                                    <DotButton
                                        key={`dot-${slide.id}`}
                                        index={index}
                                        isActive={currentSlide === index}
                                        totalSlides={slideCount}
                                        onClick={goToSlide}
                                    />
                                ))}
                            </div>
                        )}
                        <div className="absolute right-0 flex gap-2">
                            {autoPlay && (
                                <button
                                    type="button"
                                    onClick={togglePlayPause}
                                    className="rounded-ui p-3 bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                    aria-label={isManuallyPaused ? t('carousel.play') : t('carousel.pause')}>
                                    {isManuallyPaused ? (
                                        <Play className="w-6 h-6 text-primary-foreground" strokeWidth={2} />
                                    ) : (
                                        <Pause className="w-6 h-6 text-primary-foreground" strokeWidth={2} />
                                    )}
                                </button>
                            )}
                            {showNavigation && (
                                <>
                                    <NavigationButton
                                        direction="prev"
                                        onClick={() => api?.scrollPrev()}
                                        disabled={!canScrollPrev}
                                        currentSlide={currentSlide + 1}
                                        totalSlides={slideCount}
                                    />
                                    <NavigationButton
                                        direction="next"
                                        onClick={() => api?.scrollNext()}
                                        disabled={!canScrollNext}
                                        currentSlide={currentSlide + 1}
                                        totalSlides={slideCount}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="sr-only" aria-live="polite" aria-atomic="true">
                Slide {currentSlide + 1} of {slideCount}: {slideMeta[currentSlide]?.title}
            </div>
        </div>
    );
}

/**
 * Clone a Page Designer Hero component with carousel-controlled slide props merged into its
 * `data` (which <Component> spreads onto the Hero). A per-slide Hero `overlay` authored in
 * Page Designer wins over the carousel default; `priority`/`loading`/`fillHeight` are always
 * set by the carousel (they're not Page-Designer attributes).
 */
function withSlideProps(
    comp: ComponentType,
    slideProps: { overlay: string; priority: 'high' | 'auto'; loading: 'eager' | 'lazy'; fillHeight: boolean }
): ComponentType {
    const data = (comp.data as Record<string, unknown> | undefined) ?? {};
    return {
        ...comp,
        data: {
            ...data,
            // Per-slide overlay overrides the carousel default; carousel default fills in when unset.
            overlay: (data.overlay as string | undefined) ?? slideProps.overlay,
            priority: slideProps.priority,
            loading: slideProps.loading,
            fillHeight: slideProps.fillHeight,
        },
        // SCAPI types Component.data as Record<string, never>; the runtime payload is arbitrary
        // attribute data, so cast through unknown to attach the carousel-controlled slide props.
    } as unknown as ComponentType;
}

const DotButton = React.memo(
    ({
        index,
        isActive,
        totalSlides,
        onClick,
    }: {
        index: number;
        isActive: boolean;
        totalSlides: number;
        onClick: (index: number) => void;
    }): ReactElement => (
        <button
            type="button"
            onClick={() => onClick(index)}
            // The visible pip is only 8px tall, well under the 24px WCAG 2.5.8 minimum tap target.
            // Keep the pip's look but give the button a >=24x24 hit area (min-w-6 min-h-6) with the
            // pip centered inside as a decorative span, so the target the shopper can tap/click meets
            // the minimum without enlarging the indicator visually.
            className="group flex items-center justify-center min-w-6 min-h-6 rounded-ui focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            role="tab"
            aria-selected={isActive}
            aria-label={`Go to slide ${index + 1} of ${totalSlides}`}
            tabIndex={isActive ? 0 : -1}>
            <span
                aria-hidden="true"
                className={`block rounded-ui transition-all duration-300 ${
                    isActive ? 'w-8 h-2 bg-white' : 'w-2 h-2 bg-white/50 group-hover:bg-white/75'
                }`}
            />
        </button>
    )
);

DotButton.displayName = 'DotButton';

const NavigationButton = React.memo(
    ({
        direction,
        onClick,
        disabled,
        currentSlide,
        totalSlides,
    }: {
        direction: 'prev' | 'next';
        onClick: () => void;
        disabled: boolean;
        currentSlide: number;
        totalSlides: number;
    }): ReactElement => {
        const Icon = direction === 'prev' ? ChevronLeft : ChevronRight;
        const label = direction === 'prev' ? 'Previous' : 'Next';

        return (
            <button
                type="button"
                onClick={onClick}
                disabled={disabled}
                className="rounded-ui p-3 bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={`${label} slide (${currentSlide} of ${totalSlides})`}>
                <Icon className="w-6 h-6 text-primary-foreground" strokeWidth={2} />
            </button>
        );
    }
);

NavigationButton.displayName = 'NavigationButton';

/**
 * Design-mode empty state (W-23729831): the placeholder a merchant sees when an unconfigured Hero
 * Carousel is dropped into a Page Designer region. A muted surface previewing the carousel's shape —
 * a centered "No banners yet" cue framed by decorative prev/next arrows and dot indicators, so the
 * authoring affordance reads as a carousel before any Hero slides are added.
 *
 * The carousel renders NONE of the registered `Hero` Page Designer component here — importing `Hero`
 * dragged its registration side effects into the carousel's module evaluation and could hang the
 * whole PD region on MRT when an unconfigured carousel was dropped in.
 *
 * Theme-bound only (`bg-muted` / `text-muted-foreground` / `bg-background`, no hardcoded colors) so
 * it tracks the active theme. Arrows and dots are purely illustrative (`aria-hidden`,
 * non-interactive): there are no slides to navigate. Callers gate this to design mode — it is never
 * shown to live-storefront shoppers.
 */
function HeroCarouselEmptyState({ label }: { label: string }): ReactElement {
    return (
        <div data-slot="hero-carousel" className="relative w-full">
            <div
                data-slot="empty-state"
                className="relative flex h-[400px] md:h-[500px] lg:h-[600px] w-full items-center justify-center overflow-hidden rounded-ui bg-muted">
                {/* Decorative arrows, shown greyed-out/disabled — an illustrative carousel affordance,
                    not controls (there are no slides to navigate yet). */}
                <span
                    aria-hidden
                    className="absolute left-4 top-1/2 flex w-12 h-12 -translate-y-1/2 items-center justify-center rounded-ui bg-background opacity-50 shadow-sm">
                    <ChevronLeft className="w-6 h-6 text-muted-foreground" strokeWidth={2} />
                </span>
                <h2 className={cn(EMPTY_LABEL_CLASS, 'text-muted-foreground')}>{label}</h2>
                <span
                    aria-hidden
                    className="absolute right-4 top-1/2 flex w-12 h-12 -translate-y-1/2 items-center justify-center rounded-ui bg-background opacity-50 shadow-sm">
                    <ChevronRight className="w-6 h-6 text-muted-foreground" strokeWidth={2} />
                </span>
                {/* Decorative dot indicators inside the surface, floated above the bottom edge
                    (matching the configured carousel's `bottom-6` dots) so they aren't clipped by
                    the Page Designer region boundary. */}
                <div aria-hidden className="absolute bottom-6 inset-x-0 flex justify-center gap-2">
                    {Array.from({ length: 3 }, (_, i) => (
                        <span key={i} className="w-2 h-2 rounded-ui bg-muted-foreground/40" />
                    ))}
                </div>
            </div>
        </div>
    );
}

const HeroSlideContent = React.memo(({ slide, priority }: { slide: HeroSlide; priority: boolean }): ReactElement => {
    const position = normalizeOverlayPosition(slide.overlayPosition);
    const alignment = normalizeOverlayAlignment(slide.overlayAlignment);
    const { vertical, horizontal } = overlayPositionLayout(position);

    const overlayRowClass = cn(
        vertical === 'start' && 'items-start',
        vertical === 'center' && 'items-center',
        vertical === 'end' && 'items-end'
    );
    const overlayEdgePaddingClass = cn(
        vertical === 'start' && 'pt-6 sm:pt-8 md:pt-10',
        vertical === 'end' && 'pb-6 sm:pb-8 md:pb-10'
    );
    const contentBlockClass = cn('max-w-xl', horizontal === 'center' && 'mx-auto', horizontal === 'right' && 'ml-auto');
    const textAlignClass = alignment === 'left' ? 'text-left' : alignment === 'right' ? 'text-right' : 'text-center';
    const ctaJustifyClass =
        alignment === 'left' ? 'justify-start' : alignment === 'right' ? 'justify-end' : 'justify-center';

    return (
        <div className="relative w-full h-full overflow-hidden">
            <DynamicImage
                src={slide.imageUrl}
                alt={slide.imageAlt || slide.title || 'Hero Carousel'}
                widths={HERO_IMAGE_WIDTHS}
                // One full-width slide is visible per view (CarouselItem is basis-full), so slide 0 is what
                // paints first and is the LCP candidate: preload it and load it eagerly. Off-screen slides stay
                // lazy so they don't compete with the LCP image for bandwidth.
                priority={priority ? 'high' : 'auto'}
                loading={priority ? 'eager' : 'lazy'}
                className="absolute inset-0 w-full h-full"
                imageProps={{ className: 'w-full h-full object-cover' }}
            />
            {/*
             * Scrim for WCAG 1.4.3 text contrast. The overlay text is white and can be placed at any of the
             * 9 overlayPosition slots over an arbitrary merchant photo, so a directional gradient cannot
             * guarantee legibility everywhere (a top or centre heading over a bright sky measured ~1.7:1). A
             * uniform scrim floor keeps the composited background dark enough for white text over any region
             * of any image: worst case (a blown-out white region behind) is 5.7:1 for fashion and 4.7:1 for
             * cosmetic, both clearing the 4.5:1 AA minimum. The scrim recipe lives in each vertical's
             * `theme/tokens/brand.css` as `--hero-scrim` (mixed from that brand's `--brand-black` so it keeps
             * the warm/neutral tint rather than forcing pure black), so an omitted brand color can't silently
             * collapse the scrim. The image itself is unchanged. */}
            <div className="absolute inset-0 bg-[var(--hero-scrim)]" />

            <div className={cn('relative h-full flex z-20 overflow-hidden', overlayRowClass, overlayEdgePaddingClass)}>
                <div className="section-container w-full">
                    <div className={cn(contentBlockClass, textAlignClass)}>
                        <h2 className="text-6xl font-bold leading-none [letter-spacing:-1.5px] text-primary-foreground mb-4">
                            {slide.title}
                        </h2>

                        {slide.subtitle && (
                            <p className="text-lg font-normal leading-[120%] text-primary-foreground mb-8">
                                {slide.subtitle}
                            </p>
                        )}

                        <div className={cn('flex', ctaJustifyClass)}>
                            <Button
                                asChild
                                className="h-auto px-8 py-4 text-sm font-medium leading-5 text-primary-foreground">
                                <Link to={slide.ctaLink || '#'} aria-label={slide.ctaAriaLabel}>
                                    {slide.ctaText || 'Learn More'}
                                </Link>
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

HeroSlideContent.displayName = 'HeroSlideContent';

/**
 * HeroCarouselWithSuspense component provides a HeroCarousel wrapped with a Suspense boundary.
 *
 * This component automatically shows the HeroCarouselSkeleton as a fallback while the
 * HeroCarousel is loading, providing better user experience during data fetching.
 *
 * When used with a `resolve` prop, the resolved data should be an object containing
 * slides data that will be passed as props to the HeroCarousel component.
 *
 * @example
 * ```tsx
 * // Basic usage with Suspense boundary
 * <HeroCarouselWithSuspense
 *   slides={heroSlides}
 *   autoPlay={true}
 *   showDots={true}
 * />
 *
 * // Usage with promise resolution as a prop
 * <HeroCarouselWithSuspense
 *   resolve={heroDataPromise}
 *   autoPlay={true}
 * />
 *
 * // Usage in a page with streaming
 * function HomePage() {
 *   return (
 *     <div>
 *       <HeroCarouselWithSuspense resolve={heroDataPromise} />
 *       <ProductCarouselWithSuspense resolve={productsPromise} />
 *     </div>
 *   );
 * }
 * ```
 */
const HeroCarousel = withSuspense(HeroCarouselPlain, {
    fallback: (props) => <HeroCarouselSkeleton {...props} />,
});

export default HeroCarousel;

// oxlint-disable-next-line react-refresh/only-export-components
export { HeroCarouselSkeleton, HeroCarouselSkeleton as fallback };
